export const SUMMARIZED_APPOINTMENT_HEADING_REGEX =
  /^(Appointment\s+\d+)(?:\s*(\(Option\s+[^)]+\)))?\s*(?:[\u2014-]\s*(.+))?$/i;

const HTML_TAG_REGEX = /<[a-z][\s\S]*>/i;

export function isSummarizedContentHtml(content: string): boolean {
  return HTML_TAG_REGEX.test(content);
}

export function summarizedContentToPlainText(content: string): string {
  return content
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

export function summarizedContentToEditorHtml(content: string): string {
  if (isSummarizedContentHtml(content)) {
    return content;
  }

  const escaped = content
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

  return escaped.replace(/\n/g, "<br>");
}

export interface ParsedSummarizedAppointmentHeading {
  appointmentLabel: string;
  optionLabel?: string;
  timeframe?: string;
}

export function parseSummarizedAppointmentHeading(
  line: string,
): ParsedSummarizedAppointmentHeading | null {
  const match = line.trim().match(SUMMARIZED_APPOINTMENT_HEADING_REGEX);
  if (!match) return null;

  return {
    appointmentLabel: match[1],
    optionLabel: match[2]?.trim() || undefined,
    timeframe: match[3]?.trim() || undefined,
  };
}

export type SummarizedContentLineType =
  | "intro"
  | "appointment"
  | "bullet"
  | "description"
  | "cost"
  | "empty";

export interface ParsedSummarizedContentLine {
  type: SummarizedContentLineType;
  text: string;
  appointmentLabel?: string;
  optionLabel?: string;
  timeframe?: string;
}

export function parseSummarizedContent(
  raw: string,
): ParsedSummarizedContentLine[] {
  const normalized = isSummarizedContentHtml(raw)
    ? summarizedContentToPlainText(raw)
    : raw;
  const rawLines = normalized.split("\n");
  const result: ParsedSummarizedContentLine[] = [];
  let seenAppointment = false;

  for (const line of rawLines) {
    const trimmed = line.trim();

    if (trimmed === "") {
      result.push({ type: "empty", text: "" });
      continue;
    }

    const appointmentHeading = parseSummarizedAppointmentHeading(trimmed);
    if (appointmentHeading) {
      seenAppointment = true;
      result.push({
        type: "appointment",
        text: trimmed,
        appointmentLabel: appointmentHeading.appointmentLabel,
        optionLabel: appointmentHeading.optionLabel,
        timeframe: appointmentHeading.timeframe,
      });
      continue;
    }

    if (trimmed.startsWith("\u2022") || trimmed.startsWith("-")) {
      result.push({
        type: "bullet",
        text: trimmed.replace(/^[\u2022-]\s*/, ""),
      });
      continue;
    }

    if (/^total care cost:/i.test(trimmed)) {
      result.push({ type: "cost", text: trimmed });
      continue;
    }

    if (!seenAppointment) {
      result.push({ type: "intro", text: trimmed });
    } else {
      result.push({ type: "description", text: trimmed });
    }
  }

  return result;
}
